import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Upload, FileText, FileImage, FileSpreadsheet, File as FileIcon, Download, Eye, Clock, Search, Filter, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { documentsAPI, DOCUMENT_STATUS_LABELS, DOCUMENT_STATUS_COLORS } from '../../lib/documentsAPI';
import { formatFileSize, getFileIcon, formatRelativeTime, downloadBlob } from '../../lib/fileUtils';
import { formatAUDate } from '../../lib/dateUtils';

export function DocumentsTab({ clientId }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [selectedFile, setSelectedFile] = useState(null);
  const [visibility, setVisibility] = useState('staff_only');
  const [labels, setLabels] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
  const [selectedDocVersions, setSelectedDocVersions] = useState([]);

  useEffect(() => {
    loadDocuments();
  }, [clientId, visibilityFilter]);

  const loadDocuments = async () => {
    try {
      const params = {};
      if (visibilityFilter !== 'all') params.visibility = visibilityFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await documentsAPI.list(clientId, params);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('visibility', visibility);
      formData.append('labels', labels);

      const response = await documentsAPI.upload(clientId, formData);
      toast.success(response.data.message);
      
      setSelectedFile(null);
      setLabels('');
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentsAPI.download(doc.id);
      downloadBlob(response.data, doc.filename);
      toast.success('Document downloaded');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleViewVersions = async (doc) => {
    try {
      const response = await documentsAPI.versions(doc.id);
      setSelectedDocVersions(response.data);
      setVersionsDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load versions');
    }
  };

  const handleStatusChange = async (doc, status) => {
    try {
      await documentsAPI.setStatus(doc.id, status);
      toast.success('Status updated');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleClassify = async (doc) => {
    try {
      const res = await documentsAPI.classify(doc.id);
      toast.success(`Classified as: ${res.data.category || 'other'}`);
      loadDocuments();
    } catch (error) {
      toast.error('Failed to classify document');
    }
  };

  const filteredDocuments = documents.filter(doc => {
    if (!searchTerm) return true;
    return doc.filename.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getIconComponent = (mimeType) => {
    const iconName = getFileIcon(mimeType);
    const icons = {
      FileText,
      FileImage,
      FileSpreadsheet,
      FileIcon
    };
    return icons[iconName] || FileIcon;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Upload Document</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 transition-colors ${
              dragActive ? 'border-primary bg-accent' : 'border-border'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center justify-center text-center">
              <Upload className={`w-12 h-12 mb-4 ${dragActive ? 'text-primary' : 'text-muted-foreground/70'}`} />
              <p className="text-sm text-muted-foreground mb-2">
                {selectedFile ? selectedFile.name : 'Drag & drop a file here, or click to select'}
              </p>
              <input
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <Label htmlFor="file-upload">
                <Button variant="outline" asChild className="cursor-pointer">
                  <span>Browse Files</span>
                </Button>
              </Label>
            </div>

            {selectedFile && (
              <div className="mt-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Visibility</Label>
                    <Select value={visibility} onValueChange={setVisibility}>
                      <SelectTrigger data-testid="doc-visibility-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff_only">Staff Only</SelectItem>
                        <SelectItem value="client_visible">Client Visible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Labels (comma-separated)</Label>
                    <Input
                      placeholder="BAS Q1 FY25, Tax Return"
                      value={labels}
                      onChange={(e) => setLabels(e.target.value)}
                      data-testid="doc-labels-input"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="w-full bg-primary hover:bg-primary/90"
                  data-testid="upload-document-button"
                >
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Documents</CardTitle>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                  data-testid="doc-search-input"
                />
              </div>
              <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
                <SelectTrigger className="w-[160px]" data-testid="doc-visibility-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Documents</SelectItem>
                  <SelectItem value="staff_only">Staff Only</SelectItem>
                  <SelectItem value="client_visible">Client Visible</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <p>No documents uploaded yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold text-foreground/90">File</TableHead>
                  <TableHead className="font-semibold text-foreground/90">Status</TableHead>
                  <TableHead className="font-semibold text-foreground/90">Category</TableHead>
                  <TableHead className="font-semibold text-foreground/90">Visibility</TableHead>
                  <TableHead className="font-semibold text-foreground/90">Uploaded</TableHead>
                  <TableHead className="font-semibold text-foreground/90 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocuments.map((doc) => {
                  const IconComponent = getIconComponent(doc.mime_type);
                  return (
                    <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <IconComponent className="w-5 h-5 text-muted-foreground" />
                          <span className="text-foreground">{doc.filename}</span>
                          {doc.version > 1 && (
                            <Badge variant="outline" className="text-xs">v{doc.version}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select value={doc.status || 'uploaded'} onValueChange={(v) => handleStatusChange(doc, v)}>
                          <SelectTrigger className="h-7 w-[150px] text-xs" data-testid={`doc-status-${doc.id}`}>
                            <SelectValue>
                              <Badge className={`${DOCUMENT_STATUS_COLORS[doc.status || 'uploaded']} text-xs`}>
                                {DOCUMENT_STATUS_LABELS[doc.status || 'uploaded']}
                              </Badge>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(DOCUMENT_STATUS_LABELS).map(([k, label]) => (
                              <SelectItem key={k} value={k}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {doc.category ? (
                          <Badge variant="outline" className="text-xs capitalize">{doc.category.replace(/_/g, ' ')}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground/70">—</span>
                        )}
                        {doc.obligation_type && (
                          <div className="flex flex-wrap gap-1 mt-1" data-testid={`doc-compliance-tags-${doc.id}`}>
                            {[doc.obligation_type, doc.financial_year && `FY ${doc.financial_year}`, doc.period_label].filter(Boolean).map((x) => <Badge key={x} variant="secondary" className="text-[10px]">{x}</Badge>)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={doc.visibility === 'staff_only' ? 'bg-muted text-foreground/90' : 'bg-blue-100 text-blue-700'}>
                          {doc.visibility === 'staff_only' ? 'Staff Only' : 'Client Visible'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatAUDate(doc.uploaded_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleClassify(doc)}
                            title="AI classify"
                            data-testid={`classify-doc-${doc.id}`}
                          >
                            <Sparkles className="w-4 h-4 text-violet-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(doc)}
                            data-testid={`download-doc-${doc.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewVersions(doc)}
                            data-testid={`versions-doc-${doc.id}`}
                          >
                            <Clock className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Versions Dialog */}
      <Dialog open={versionsDialogOpen} onOpenChange={setVersionsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {selectedDocVersions.map((version) => (
              <div key={version.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Version {version.version}</span>
                    {version.is_latest && (
                      <Badge className="bg-primary text-white">Latest</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {formatAUDate(version.uploaded_at)} • {formatFileSize(version.size_bytes)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(version)}
                >
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
